import type { BadgeVariant } from '../../components/symphony/ui/Badge';
import type { WorldActor, WorldTask } from './living-world';

/** Persisted task status and current animation are different: an idle loop isn't necessarily awaiting a reply. */
export function worldTaskBadge(actor: WorldActor, task?: WorldTask, recorded = false): { labelKey: string; variant: BadgeVariant } {
  if (actor.state === 'needsYou' && (recorded || !['COMPLETED', 'CANCELLED', 'MOOT', 'FAILED'].includes(task?.status ?? '')))
    return { labelKey: 'tasks.status.actionNeeded', variant: 'waiting-on-you' };
  if (!recorded && task?.status) {
    const persisted: Record<string, { labelKey: string; variant: BadgeVariant }> = {
      PENDING: { labelKey: 'home.world.pending', variant: 'waiting-on-a-reply' },
      WAITING: { labelKey: 'tasks.status.waiting', variant: 'waiting-on-a-reply' },
      BLOCKED: { labelKey: 'tasks.status.blocked', variant: 'waiting-on-a-reply' },
      IN_PROGRESS: { labelKey: 'tasks.status.activeTask', variant: 'in-progress' },
      IN_REVIEW: { labelKey: 'tasks.status.inReview', variant: 'in-progress' },
      COMPLETED: { labelKey: 'tasks.status.done', variant: 'done' },
      FAILED: { labelKey: 'tasks.status.failed', variant: 'failed' },
      CANCELLED: { labelKey: 'tasks.status.cancelled', variant: 'waiting-on-a-reply' },
      MOOT: { labelKey: 'tasks.status.retired', variant: 'waiting-on-a-reply' },
    };
    if (persisted[task.status]) return persisted[task.status];
  }
  if (actor.state === 'done') return { labelKey: 'tasks.status.done', variant: 'done' };
  if (actor.state === 'failed') return { labelKey: 'tasks.status.failed', variant: 'failed' };
  if (actor.state === 'queued') return { labelKey: 'home.world.pending', variant: 'waiting-on-a-reply' };
  if (actor.state === 'scheduled') return { labelKey: 'routines.schedule.scheduled', variant: 'waiting-on-a-reply' };
  if (actor.state === 'waiting') return { labelKey: 'home.world.idle', variant: 'waiting-on-a-reply' };
  if (actor.state === 'unknown') return { labelKey: 'home.world.unavailable', variant: 'waiting-on-a-reply' };
  // The fall-through is an actor that is none of done/failed/queued/scheduled/waiting/unknown — i.e.
  // one actually running, which is what `Working` means. The PERSISTED map above keeps `Active Task`,
  // because a stored status says nothing about whether a loop is live.
  return { labelKey: actor.state === 'review' ? 'tasks.status.inReview' : 'tasks.status.working', variant: 'in-progress' };
}

export function tasksForWorldAgent(actors: WorldActor[], agentId: string): WorldActor[] {
  return actors.filter((actor) => actor.agentId === agentId || (actor.state === 'review' && actor.reviewerId === agentId));
}
